import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useCreateFlowButton, useUpdateFlowButton } from '@/hooks/useFlowButtons';
import type {
  IFlowButton,
  IFlowButtonCreateRequest,
  FlowType,
  FlowButtonShape,
  FlowButtonColor,
} from '@/types/flowbutton';
import { FLOW_TYPE_LABELS, FLOW_BUTTON_COLORS } from '@/types/flowbutton';

interface FlowButtonCreatorProps {
  open: boolean;
  onClose: () => void;
  editButton?: IFlowButton;
  defaultPage: string;
}

const FLOW_TYPES: FlowType[] = ['docflow', 'part_create', 'bom_maintain', 'purchase_order', 'sale_order', 'invoice_process', 'batch_invoice_process'];
const SHAPES: FlowButtonShape[] = ['pill', 'circle', 'square', 'rounded-rect'];
const COLORS: FlowButtonColor[] = ['blue', 'emerald', 'amber', 'rose', 'purple', 'cyan', 'gray'];

const FlowButtonCreator: React.FC<FlowButtonCreatorProps> = ({ open, onClose, editButton, defaultPage }) => {
  const createFlowButton = useCreateFlowButton();
  const updateFlowButton = useUpdateFlowButton();

  const [name, setName] = useState(editButton?.name || '');
  const [subtitle, setSubtitle] = useState(editButton?.subtitle || '');
  const [flowType, setFlowType] = useState<FlowType>(editButton?.flow_type || 'part_create');
  const [shape, setShape] = useState<FlowButtonShape>(editButton?.shape || 'pill');
  const [color, setColor] = useState<FlowButtonColor>(editButton?.color || 'blue');
  const [pageScope, setPageScope] = useState<'this' | 'all'>(
    editButton?.page === '*' ? 'all' : 'this'
  );

  useEffect(() => {
    if (editButton) {
      setName(editButton.name);
      setSubtitle(editButton.subtitle || '');
      setFlowType(editButton.flow_type);
      setShape(editButton.shape);
      setColor(editButton.color);
      setPageScope(editButton.page === '*' ? 'all' : 'this');
    }
  }, [editButton]);

  const handleSubmit = async () => {
    if (!name.trim()) return;

    const data: IFlowButtonCreateRequest = {
      name: name.trim(),
      subtitle: subtitle.trim() || undefined,
      flow_type: flowType,
      flow_config: editButton?.flow_config || {},
      shape,
      color,
      page: pageScope === 'all' ? '*' : defaultPage,
    };

    try {
      if (editButton) {
        await updateFlowButton.mutateAsync({ id: editButton.id, data });
      } else {
        await createFlowButton.mutateAsync(data);
      }
      onClose();
    } catch {
      // Error handled by mutation
    }
  };

  if (!open) return null;

  const colorConfig = FLOW_BUTTON_COLORS[color];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {editButton ? 'Edit Flow Button' : 'Add Flow Button'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Flow Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Flow Type</label>
            <select
              value={flowType}
              onChange={(e) => setFlowType(e.target.value as FlowType)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
            >
              {FLOW_TYPES.map((ft) => (
                <option key={ft} value={ft}>
                  {FLOW_TYPE_LABELS[ft].en} ({FLOW_TYPE_LABELS[ft].zh})
                </option>
              ))}
            </select>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Scan Invoice"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
            />
          </div>

          {/* Subtitle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subtitle (optional)</label>
            <input
              type="text"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="e.g., Extract & create"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
            />
          </div>

          {/* Shape */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Shape</label>
            <div className="flex gap-2">
              {SHAPES.map((s) => (
                <button
                  key={s}
                  onClick={() => setShape(s)}
                  className={`px-3 py-1.5 text-xs rounded-lg border ${
                    shape === s
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                      : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Color</label>
            <div className="flex gap-2">
              {COLORS.map((c) => {
                const cc = FLOW_BUTTON_COLORS[c];
                return (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    className={`w-8 h-8 rounded-full ${cc.bg} ${
                      color === c ? 'ring-2 ring-offset-2 ' + cc.ring : ''
                    }`}
                    title={c}
                  />
                );
              })}
            </div>
          </div>

          {/* Page Scope */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Show on</label>
            <div className="flex gap-3">
              <label className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="radio"
                  checked={pageScope === 'this'}
                  onChange={() => setPageScope('this')}
                  className="text-indigo-600"
                />
                This page ({defaultPage})
              </label>
              <label className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="radio"
                  checked={pageScope === 'all'}
                  onChange={() => setPageScope('all')}
                  className="text-indigo-600"
                />
                All pages
              </label>
            </div>
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Preview</label>
            <div className="flex items-center justify-center py-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <div className={`${colorConfig.bg} ${colorConfig.text} shadow-lg border-2 ${colorConfig.border} text-sm font-medium flex items-center justify-center gap-1.5 ${
                shape === 'circle' ? 'rounded-full w-12 h-12' :
                shape === 'square' ? 'rounded-md w-12 h-12' :
                shape === 'pill' ? 'rounded-full px-4 py-2' :
                'rounded-lg px-4 py-2'
              }`}>
                <span className="text-xs">{name || 'Button'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!name.trim() || createFlowButton.isLoading || updateFlowButton.isLoading}
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {editButton ? 'Save Changes' : 'Create Button'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlowButtonCreator;
